import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import scrollToElement from 'scroll-to-element'
import AddressForm from 'components/AddressForm'
import FullNameField from 'components/form/FullNameField'
import EmailField from 'components/form/EmailField'
import BrandedButton from 'components/BrandedButton'
import { validateForm } from 'utilities/formValidator.util'
import getUrlParameter from 'utilities/urlParams.util'
import { ADDRESS_TEXT_KEY } from 'components/AutocompleteAddress/constants'
import {
  STREET_NUMBER_KEY,
  STREET_NAME_KEY,
  STREET_TYPE_KEY,
  SUBURB_KEY,
  STATE_KEY,
  POSTCODE_KEY,
} from 'components/Address/constants'
import getCookie from 'utilities/cookie'
import { trackAbandonments } from 'utilities/tracking'
import { sendEvent } from 'hoc/WithInputAnalytics/utils'
import withInputAnalytics from 'hoc/WithInputAnalytics'
import { GA_ACTION_COMPLETED } from 'hoc/WithInputAnalytics/constants'
import styles from './styles.scss'
import {
  setLeadDetails,
  setResponseMessage,
  submitLead,
  setFieldErrors,
  setManualAddress,
} from './actions'
import {
  GA_EVENT_CATEGORY,
  COOKIE_KEY_NAME,
  COOKIE_KEY_EMAIL,
  COOKIE_KEY_ADDRESS,
  LEAD_SUCCESS_CONTAINER_ID,
  LEAD_NAME_KEY,
  LEAD_EMAIL_KEY,
} from './constants'

const TrackedFullNameField = withInputAnalytics(GA_EVENT_CATEGORY)(FullNameField)
const TrackedEmailField = withInputAnalytics(GA_EVENT_CATEGORY)(EmailField)

class LeadGen extends Component {
  constructor(props) {
    super(props)
    // We handle state here locally as storing these in Redux for onChange events is very expensive!
    this.state = {
      fieldValues: { [LEAD_NAME_KEY]: '', [LEAD_EMAIL_KEY]: '' },
    }
    this.leadNameRef = null
    this.addressFocusRef = React.createRef()
  }

  // Grab some query string values
  componentDidMount() {
    this.populateDataFromQueryStrings()
    trackAbandonments()
  }

  /* Full Name Handlers */
  onFieldChange = (value, fieldname) => {
    this.setState({
      fieldValues: {
        ...this.state.fieldValues, [fieldname]: value,
      },
    })
  }
  onFieldChangeHandler = event => this.onFieldChange(event.target.value, event.target.name)

  onFieldBlur = (event) => {
    if (this.props.fieldValues[event.target.name] !== this.state.fieldValues[event.target.name]) {
      this.props.setLeadDetails({ [event.target.name]: event.target.value })
    }
  }

  onAddressSelect = (selected) => {
    this.props.setLeadDetails({ ...selected })
  }

  onAddressUpdated = (data) => {
    this.props.setLeadDetails({ ...data.fieldValues })
  }

  /* Handle Submission */
  onLeadSubmit = (event) => {
    event.preventDefault()
    this.props.setResponseMessage('')

    const valid = validateForm(this.props.fieldValues, this.props.manualAddress)

    if (valid === true) {
      /* Clear any form errors */
      this.props.setFieldErrors({})
      // Track completion of field
      const eventLabel = `Address ${this.props.manualAddress ? '(Manual)' : '(Autocomplete)'}`
      sendEvent(eventLabel, GA_ACTION_COMPLETED, GA_EVENT_CATEGORY)
      this.props.submitLead(this.props.fieldValues, this.props.manualAddress, this.props.checkoutPage)
    } else {
      this.props.setFieldErrors(valid)
    }
  }

  clearEmailErrors = () => this.props.clearFieldError(LEAD_EMAIL_KEY)

  populateDataFromQueryStrings = () => {
    const qsName = getCookie(COOKIE_KEY_NAME)
    const qsEmail = getCookie(COOKIE_KEY_EMAIL)
    const qsAddress = getCookie(COOKIE_KEY_ADDRESS)

    const basicFieldsPayload = {
      [LEAD_NAME_KEY]: qsName,
      [LEAD_EMAIL_KEY]: qsEmail,
      [ADDRESS_TEXT_KEY]: qsAddress,
    }

    // TODO: Disable in production
    const manual = !!getUrlParameter('manual')
    if (manual) {
      const typeMapping = {
        AV: 'Avenue', DR: 'Drive', ST: 'Street', RD: 'Road',
      }
      basicFieldsPayload[STREET_NUMBER_KEY] = getUrlParameter(STREET_NUMBER_KEY)
      basicFieldsPayload[STREET_NAME_KEY] = getUrlParameter(STREET_NAME_KEY)
      basicFieldsPayload[STREET_TYPE_KEY] = typeMapping[getUrlParameter(STREET_TYPE_KEY)]
      basicFieldsPayload[SUBURB_KEY] = getUrlParameter(SUBURB_KEY)
      basicFieldsPayload[STATE_KEY] = getUrlParameter(STATE_KEY)
      basicFieldsPayload[POSTCODE_KEY] = getUrlParameter(POSTCODE_KEY)
      this.props.setManualAddress(true)
    }

    // Local state is set here
    this.setState({
      fieldValues: { ...this.state.fieldValues, ...basicFieldsPayload },
    })

    // Set redux props (whats sent to server)
    this.props.setLeadDetails(basicFieldsPayload)

    if (qsEmail || qsName) {
      if (qsAddress.length !== 0) {
        this.addressFocusRef.current.search(qsAddress)
      }
      setTimeout(() => this.addressFocusRef.current.focus(), 100)
    } else if (window.location.hash) {
      // Only focus if an anchor exists
      if (this.leadNameRef) this.leadNameRef.focus()
    }
  }

  render() {
    const {
      leadGenReducer,
      fieldError,
      manualAddress,
      submitLabel,
      termsText,
    } = this.props
    const {
      isLoading,
      success,
      message,
    } = leadGenReducer

    const isFormCompleteClass = success ? styles.form_complete : ''
    const responseMessageElement = message && !success
      ? <p className={styles.form_error}>{message}</p>
      : null
    const isManualAddressClass = manualAddress ? styles.manual_address : ''
    const termsConditionsElement = !termsText ? null : (
      // eslint-disable-next-line react/no-danger
      <div className={styles.terms} dangerouslySetInnerHTML={{ __html: termsText }} />
    )

    if (success) {
      scrollToElement(document.getElementById(LEAD_SUCCESS_CONTAINER_ID), {
        offset: -100,
        ease: 'linear',
        duration: 250,
      })
    }

    return (
      <form
        className={`${styles.container} ${isFormCompleteClass} ${isManualAddressClass}`}
        onSubmit={this.onLeadSubmit}
        method="POST"
      >
        <fieldset className={styles.form_container}>
          <legend>Signup for Kogan Internet:</legend>
          <div className={styles.field_name}>
            <TrackedFullNameField
              fieldName={LEAD_NAME_KEY}
              inputRef={(r) => { this.leadNameRef = r }}
              name={LEAD_NAME_KEY}
              onChange={this.onFieldChangeHandler}
              onBlur={this.onFieldBlur}
              value={this.state.fieldValues[LEAD_NAME_KEY]}
              errorMessage={fieldError[LEAD_NAME_KEY]}
              loading={isLoading}
            />
          </div>
          <div className={styles.field_email}>
            <TrackedEmailField
              fieldName={LEAD_EMAIL_KEY}
              onChange={this.onFieldChangeHandler}
              onBlur={this.onFieldBlur}
              onFocus={this.clearEmailErrors}
              value={this.state.fieldValues[LEAD_EMAIL_KEY]}
              errorMessage={fieldError[LEAD_EMAIL_KEY]}
              loading={isLoading}
            />
          </div>
          <div className={styles.field_break} />
          <div className={
            manualAddress ? 'c-form-field--lesspadding' : styles.field_address
          }
          >
            <AddressForm
              ref={this.addressFocusRef}
              loading={isLoading}
              manual={manualAddress}
              fieldErrors={fieldError}
              fieldValues={this.props.fieldValues}
              onAddressSelected={this.onAddressSelect}
              onFieldUpdated={this.props.setLeadDetails}
              onManualEntry={this.props.setManualAddress}
              gaCategory={GA_EVENT_CATEGORY}
            />
          </div>
          <div className={styles.field_submit}>
            <BrandedButton labelText={submitLabel} loading={isLoading} fitContent />
          </div>
          {termsConditionsElement}
          {responseMessageElement}
        </fieldset>
        <div className={styles.success_container} id={LEAD_SUCCESS_CONTAINER_ID}>
          <h2>Thanks for joining the Kogan Internet waitlist.</h2>
          <p>We will send updates to <strong>{this.props.fieldValues.email}</strong></p>
          <p><a href="https://www.kogan.com/">Click here</a> to shop the latest deals from Kogan.com</p>
        </div>
      </form>
    )
  }
}

LeadGen.propTypes = {
  leadGenReducer: PropTypes.shape({
    isLoading: PropTypes.bool,
    success: PropTypes.bool,
    message: PropTypes.string,
  }),
  fieldValues: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  fieldError: PropTypes.object,
  manualAddress: PropTypes.bool,
  submitLabel: PropTypes.string,
  termsText: PropTypes.string,
  checkoutPage: PropTypes.string,

  setResponseMessage: PropTypes.func,
  submitLead: PropTypes.func,
  setFieldErrors: PropTypes.func,
  setLeadDetails: PropTypes.func,
  setManualAddress: PropTypes.func,
  clearFieldError: PropTypes.func,
}

LeadGen.defaultProps = {
  leadGenReducer: {
    isLoading: false,
    success: false,
    message: 'Just a message',
  },
  fieldValues: {},
  fieldError: {},
  manualAddress: false,
  submitLabel: 'Join Waitlist',
  termsText: null,
  checkoutPage: null,

  setResponseMessage: f => f,
  submitLead: f => f,
  setFieldErrors: f => f,
  setLeadDetails: f => f,
  setManualAddress: f => f,
  clearFieldError: f => f,
}

function mapStateToProps(state) {
  // whatever is returned will show up as props
  return {
    leadGenReducer: state.leadGenReducer,
    fieldValues: state.leadGenReducer.fieldValues,
    fieldError: state.leadGenReducer.fieldError,
    manualAddress: state.leadGenReducer.manualAddress,
  }
}

// Anything returned from this function will end up as props
function mapDispatchToProps(dispatch) {
  // Whenever dispatch is called the result should be passed to all of the reducers
  return bindActionCreators(
    {
      setLeadDetails,
      setResponseMessage,
      submitLead,
      setFieldErrors,
      setManualAddress,
    },
    dispatch
  )
}

// To promote a component to a container (smart component) - it needs
// to know about this new dispatch method. Make it available as a prop.
export default connect(mapStateToProps, mapDispatchToProps)(LeadGen)



// WEBPACK FOOTER //
// ./containers/LeadGen/index.jsx